// Static structure for the Seller, Practitioner & Place Agreement page.
// Reflects Agreement v3.0 (Full Integration: Agency Structure, Product Order
// Lifecycle, Cancellations, Returns & Disputes).
//
// Kept as plain data (instead of PageBuilder / hosted assets) so the page
// no longer depends on a fetched "terms-of-service" page asset.
//
// All prose is referenced by translation message id (see en.json / th.json,
// keys prefixed "AgreementPage.") so the content can be localized.
// Non-linguistic data (section numbers, emails, URLs, the physical address)
// is kept as plain values and rendered as-is in every locale.

export const companyInfo = {
  name: 'Metta People Co., Ltd. (Thailand)',
  registrationLabelId: 'AgreementPage.companyRegistrationLabel',
  registrationNumber: '0105567098617',
};

// Block types used below:
// p        -> paragraph, { id }
// ul / ol  -> list, { itemIds: [id, ...] }
// box      -> bordered sub-section box, { number, titleId, body }
//             body is either { id } (plain paragraph) or
//             { introId?, paragraphs?: [id,...], list?, orderedList?,
//               table?, outroId? } (ids/table only)
// callout  -> highlighted panel, { titleId?, textId }
// table    -> refund/cancellation table, { headerIds: [...], rows: [[id, ...], ...] }
// contact  -> section 24 contact cards, { cards: [{ headingId, rows: [{ labelId?, value }] }] }

export const agreementSections = [
  {
    number: '1',
    titleId: 'AgreementPage.s1.title',
    accentBar: true,
    blocks: [
      { type: 'p', id: 'AgreementPage.s1.b1' },
      { type: 'p', id: 'AgreementPage.s1.b2' },
      { type: 'p', id: 'AgreementPage.s1.b3' },
      { type: 'p', id: 'AgreementPage.s1.b4' },
      { type: 'p', id: 'AgreementPage.s1.b5' },
    ],
  },
  {
    number: '2',
    titleId: 'AgreementPage.s2.title',
    gradient: true,
    blocks: [
      {
        type: 'box',
        body: {
          table: {
            headerIds: ['AgreementPage.s2.table.h1', 'AgreementPage.s2.table.h2'],
            rows: Array.from({ length: 25 }, (_, i) => [
              `AgreementPage.s2.table.r${i + 1}c1`,
              `AgreementPage.s2.table.r${i + 1}c2`,
            ]),
          },
        },
      },
    ],
  },
  {
    number: '3',
    titleId: 'AgreementPage.s3.title',
    accentBar: true,
    blocks: [
      { type: 'callout', textId: 'AgreementPage.s3.callout.text' },
      {
        type: 'box',
        number: '3.1',
        titleId: 'AgreementPage.s3.b1.title',
        body: { id: 'AgreementPage.s3.b1.body' },
      },
      {
        type: 'box',
        number: '3.2',
        titleId: 'AgreementPage.s3.b2.title',
        body: { id: 'AgreementPage.s3.b2.body' },
      },
      {
        type: 'box',
        number: '3.3',
        titleId: 'AgreementPage.s3.b3.title',
        body: { introId: 'AgreementPage.s3.b3.intro', outroId: 'AgreementPage.s3.b3.outro' },
      },
      {
        type: 'box',
        number: '3.4',
        titleId: 'AgreementPage.s3.b4.title',
        body: { id: 'AgreementPage.s3.b4.body' },
      },
      {
        type: 'box',
        number: '3.5',
        titleId: 'AgreementPage.s3.b5.title',
        body: {
          introId: 'AgreementPage.s3.b5.intro',
          paragraphs: ['AgreementPage.s3.b5.mid'],
          list: [
            'AgreementPage.s3.b5.list1',
            'AgreementPage.s3.b5.list2',
            'AgreementPage.s3.b5.list3',
            'AgreementPage.s3.b5.list4',
          ],
          outroId: 'AgreementPage.s3.b5.outro',
        },
      },
    ],
  },
  {
    number: '4',
    titleId: 'AgreementPage.s4.title',
    blocks: [
      {
        type: 'box',
        number: '4.1',
        titleId: 'AgreementPage.s4.b1.title',
        body: { id: 'AgreementPage.s4.b1.body' },
      },
      {
        type: 'box',
        number: '4.2',
        titleId: 'AgreementPage.s4.b2.title',
        body: { id: 'AgreementPage.s4.b2.body' },
      },
      {
        type: 'box',
        number: '4.3',
        titleId: 'AgreementPage.s4.b3.title',
        body: { id: 'AgreementPage.s4.b3.body' },
      },
      {
        type: 'box',
        number: '4.4',
        titleId: 'AgreementPage.s4.b4.title',
        body: { id: 'AgreementPage.s4.b4.body' },
      },
      {
        type: 'box',
        number: '4.5',
        titleId: 'AgreementPage.s4.b5.title',
        body: { introId: 'AgreementPage.s4.b5.intro', outroId: 'AgreementPage.s4.b5.outro' },
      },
    ],
  },
  {
    number: '5',
    titleId: 'AgreementPage.s5.title',
    gradient: true,
    blocks: [
      {
        type: 'box',
        number: '5.1',
        titleId: 'AgreementPage.s5.b1.title',
        body: { id: 'AgreementPage.s5.b1.body' },
      },
      {
        type: 'box',
        number: '5.2',
        titleId: 'AgreementPage.s5.b2.title',
        body: { id: 'AgreementPage.s5.b2.body' },
      },
      {
        type: 'box',
        number: '5.3',
        titleId: 'AgreementPage.s5.b3.title',
        body: {
          introId: 'AgreementPage.s5.b3.intro',
          list: [
            'AgreementPage.s5.b3.list1',
            'AgreementPage.s5.b3.list2',
            'AgreementPage.s5.b3.list3',
            'AgreementPage.s5.b3.list4',
            'AgreementPage.s5.b3.list5',
            'AgreementPage.s5.b3.list6',
            'AgreementPage.s5.b3.list7',
            'AgreementPage.s5.b3.list8',
            'AgreementPage.s5.b3.list9',
          ],
        },
      },
      {
        type: 'box',
        number: '5.4',
        titleId: 'AgreementPage.s5.b4.title',
        body: { id: 'AgreementPage.s5.b4.body' },
      },
    ],
  },
  {
    number: '6',
    titleId: 'AgreementPage.s6.title',
    blocks: [
      {
        type: 'box',
        number: '6.1',
        titleId: 'AgreementPage.s6.b1.title',
        body: {
          introId: 'AgreementPage.s6.b1.intro',
          table: {
            headerIds: [
              'AgreementPage.s6.b1.table.h1',
              'AgreementPage.s6.b1.table.h2',
              'AgreementPage.s6.b1.table.h3',
            ],
            rows: [
              [
                'AgreementPage.s6.b1.table.r1c1',
                'AgreementPage.s6.b1.table.r1c2',
                'AgreementPage.s6.b1.table.r1c3',
              ],
              [
                'AgreementPage.s6.b1.table.r2c1',
                'AgreementPage.s6.b1.table.r2c2',
                'AgreementPage.s6.b1.table.r2c3',
              ],
              [
                'AgreementPage.s6.b1.table.r3c1',
                'AgreementPage.s6.b1.table.r3c2',
                'AgreementPage.s6.b1.table.r3c3',
              ],
            ],
          },
          outroId: 'AgreementPage.s6.b1.outro',
        },
      },
      {
        type: 'box',
        number: '6.2',
        titleId: 'AgreementPage.s6.b2.title',
        body: { introId: 'AgreementPage.s6.b2.intro', outroId: 'AgreementPage.s6.b2.outro' },
      },
      {
        type: 'box',
        number: '6.3',
        titleId: 'AgreementPage.s6.b3.title',
        body: {
          introId: 'AgreementPage.s6.b3.intro',
          list: ['AgreementPage.s6.b3.list1', 'AgreementPage.s6.b3.list2'],
          outroId: 'AgreementPage.s6.b3.outro',
        },
      },
      {
        type: 'box',
        number: '6.4',
        titleId: 'AgreementPage.s6.b4.title',
        body: {
          introId: 'AgreementPage.s6.b4.intro',
          list: [
            'AgreementPage.s6.b4.list1',
            'AgreementPage.s6.b4.list2',
            'AgreementPage.s6.b4.list3',
            'AgreementPage.s6.b4.list4',
            'AgreementPage.s6.b4.list5',
          ],
        },
      },
      {
        type: 'box',
        number: '6.5',
        titleId: 'AgreementPage.s6.b5.title',
        body: { introId: 'AgreementPage.s6.b5.intro', outroId: 'AgreementPage.s6.b5.outro' },
      },
      {
        type: 'box',
        number: '6.6',
        titleId: 'AgreementPage.s6.b6.title',
        body: { id: 'AgreementPage.s6.b6.body' },
      },
      {
        type: 'box',
        number: '6.7',
        titleId: 'AgreementPage.s6.b7.title',
        body: { id: 'AgreementPage.s6.b7.body' },
      },
    ],
  },
  {
    number: '7',
    titleId: 'AgreementPage.s7.title',
    accentBar: true,
    blocks: [
      { type: 'p', id: 'AgreementPage.s7.intro' },
      {
        type: 'box',
        number: '7.1',
        titleId: 'AgreementPage.s7.b1.title',
        body: {
          table: {
            headerIds: [
              'AgreementPage.s7.b1.table.h1',
              'AgreementPage.s7.b1.table.h2',
              'AgreementPage.s7.b1.table.h3',
            ],
            rows: Array.from({ length: 7 }, (_, i) => [
              `AgreementPage.s7.b1.table.r${i + 1}c1`,
              `AgreementPage.s7.b1.table.r${i + 1}c2`,
              `AgreementPage.s7.b1.table.r${i + 1}c3`,
            ]),
          },
        },
      },
      {
        type: 'box',
        number: '7.2',
        titleId: 'AgreementPage.s7.b2.title',
        body: { id: 'AgreementPage.s7.b2.body' },
      },
      {
        type: 'box',
        number: '7.3',
        titleId: 'AgreementPage.s7.b3.title',
        body: { id: 'AgreementPage.s7.b3.body' },
      },
      {
        type: 'box',
        number: '7.4',
        titleId: 'AgreementPage.s7.b4.title',
        body: { id: 'AgreementPage.s7.b4.body' },
      },
      {
        type: 'box',
        number: '7.5',
        titleId: 'AgreementPage.s7.b5.title',
        body: { id: 'AgreementPage.s7.b5.body' },
      },
      {
        type: 'box',
        number: '7.6',
        titleId: 'AgreementPage.s7.b6.title',
        body: { id: 'AgreementPage.s7.b6.body' },
      },
    ],
  },
  {
    number: '8',
    titleId: 'AgreementPage.s8.title',
    gradient: true,
    blocks: [
      {
        type: 'box',
        body: {
          introId: 'AgreementPage.s8.intro',
          table: {
            headerIds: [
              'AgreementPage.s8.table.h1',
              'AgreementPage.s8.table.h2',
              'AgreementPage.s8.table.h3',
              'AgreementPage.s8.table.h4',
            ],
            rows: [
              [
                'AgreementPage.s8.table.r1c1',
                'AgreementPage.s8.table.r1c2',
                'AgreementPage.s8.table.r1c3',
                'AgreementPage.s8.table.r1c4',
              ],
              [
                'AgreementPage.s8.table.r2c1',
                'AgreementPage.s8.table.r2c2',
                'AgreementPage.s8.table.r2c3',
                'AgreementPage.s8.table.r2c4',
              ],
              [
                'AgreementPage.s8.table.r3c1',
                'AgreementPage.s8.table.r3c2',
                'AgreementPage.s8.table.r3c3',
                'AgreementPage.s8.table.r3c4',
              ],
              [
                'AgreementPage.s8.table.r4c1',
                'AgreementPage.s8.table.r4c2',
                'AgreementPage.s8.table.r4c3',
                'AgreementPage.s8.table.r4c4',
              ],
            ],
          },
          outroId: 'AgreementPage.s8.outro',
        },
      },
      {
        type: 'box',
        number: '8.1',
        titleId: 'AgreementPage.s8.b1.title',
        body: { id: 'AgreementPage.s8.b1.body' },
      },
      {
        type: 'box',
        number: '8.2',
        titleId: 'AgreementPage.s8.b2.title',
        body: { id: 'AgreementPage.s8.b2.body' },
      },
    ],
  },
  {
    number: '9',
    titleId: 'AgreementPage.s9.title',
    accentBar: true,
    blocks: [
      {
        type: 'box',
        number: '9.1',
        titleId: 'AgreementPage.s9.b1.title',
        body: { id: 'AgreementPage.s9.b1.body' },
      },
      {
        type: 'box',
        number: '9.2',
        titleId: 'AgreementPage.s9.b2.title',
        body: {
          table: {
            headerIds: ['AgreementPage.s9.b2.table.h1', 'AgreementPage.s9.b2.table.h2'],
            rows: Array.from({ length: 5 }, (_, i) => [
              `AgreementPage.s9.b2.table.r${i + 1}c1`,
              `AgreementPage.s9.b2.table.r${i + 1}c2`,
            ]),
          },
        },
      },
      {
        type: 'box',
        number: '9.3',
        titleId: 'AgreementPage.s9.b3.title',
        body: { id: 'AgreementPage.s9.b3.body' },
      },
      {
        type: 'box',
        number: '9.4',
        titleId: 'AgreementPage.s9.b4.title',
        body: { id: 'AgreementPage.s9.b4.intro' },
      },
      { type: 'callout', textId: 'AgreementPage.s9.callout.text' },
      { type: 'p', id: 'AgreementPage.s9.b4.outro' },
      {
        type: 'box',
        number: '9.5',
        titleId: 'AgreementPage.s9.b5.title',
        body: { id: 'AgreementPage.s9.b5.body' },
      },
      {
        type: 'box',
        number: '9.6',
        titleId: 'AgreementPage.s9.b6.title',
        body: {
          introId: 'AgreementPage.s9.b6.intro',
          paragraphs: ['AgreementPage.s9.b6.groundsLead'],
          list: [
            'AgreementPage.s9.b6.list1',
            'AgreementPage.s9.b6.list2',
            'AgreementPage.s9.b6.list3',
            'AgreementPage.s9.b6.list4',
            'AgreementPage.s9.b6.list5',
          ],
          outroId: 'AgreementPage.s9.b6.outro',
        },
      },
      {
        type: 'box',
        number: '9.7',
        titleId: 'AgreementPage.s9.b7.title',
        body: {
          introId: 'AgreementPage.s9.b7.intro',
          table: {
            headerIds: ['AgreementPage.s9.b7.table.h1', 'AgreementPage.s9.b7.table.h2'],
            rows: [
              ['AgreementPage.s9.b7.table.r1c1', 'AgreementPage.s9.b7.table.r1c2'],
              ['AgreementPage.s9.b7.table.r2c1', 'AgreementPage.s9.b7.table.r2c2'],
              ['AgreementPage.s9.b7.table.r3c1', 'AgreementPage.s9.b7.table.r3c2'],
            ],
          },
        },
      },
      {
        type: 'box',
        number: '9.8',
        titleId: 'AgreementPage.s9.b8.title',
        body: { id: 'AgreementPage.s9.b8.body' },
      },
      {
        type: 'box',
        number: '9.9',
        titleId: 'AgreementPage.s9.b9.title',
        body: { id: 'AgreementPage.s9.b9.body' },
      },
    ],
  },
  {
    number: '10',
    titleId: 'AgreementPage.s10.title',
    blocks: [
      {
        type: 'box',
        number: '10.1',
        titleId: 'AgreementPage.s10.b1.title',
        body: { id: 'AgreementPage.s10.b1.body' },
      },
      {
        type: 'box',
        number: '10.2',
        titleId: 'AgreementPage.s10.b2.title',
        body: { id: 'AgreementPage.s10.b2.body' },
      },
      {
        type: 'box',
        number: '10.3',
        titleId: 'AgreementPage.s10.b3.title',
        body: { id: 'AgreementPage.s10.b3.body' },
      },
      {
        type: 'box',
        number: '10.4',
        titleId: 'AgreementPage.s10.b4.title',
        body: { id: 'AgreementPage.s10.b4.body' },
      },
      {
        type: 'box',
        number: '10.5',
        titleId: 'AgreementPage.s10.b5.title',
        body: { id: 'AgreementPage.s10.b5.body' },
      },
    ],
  },
  {
    number: '11',
    titleId: 'AgreementPage.s11.title',
    gradient: true,
    blocks: [
      {
        type: 'box',
        number: '11.1',
        titleId: 'AgreementPage.s11.b1.title',
        body: { id: 'AgreementPage.s11.b1.body' },
      },
      {
        type: 'box',
        number: '11.2',
        titleId: 'AgreementPage.s11.b2.title',
        body: { introId: 'AgreementPage.s11.b2.intro', outroId: 'AgreementPage.s11.b2.outro' },
      },
      {
        type: 'box',
        number: '11.3',
        titleId: 'AgreementPage.s11.b3.title',
        body: { id: 'AgreementPage.s11.b3.intro' },
      },
      { type: 'callout', textId: 'AgreementPage.s11.callout.text' },
      {
        type: 'box',
        number: '11.4',
        titleId: 'AgreementPage.s11.b4.title',
        body: { id: 'AgreementPage.s11.b4.body' },
      },
      {
        type: 'box',
        number: '11.5',
        titleId: 'AgreementPage.s11.b5.title',
        body: { id: 'AgreementPage.s11.b5.body' },
      },
      {
        type: 'box',
        number: '11.6',
        titleId: 'AgreementPage.s11.b6.title',
        body: { id: 'AgreementPage.s11.b6.body' },
      },
    ],
  },
  {
    number: '12',
    titleId: 'AgreementPage.s12.title',
    accentBar: true,
    blocks: [
      {
        type: 'box',
        number: '12.1',
        titleId: 'AgreementPage.s12.b1.title',
        body: { id: 'AgreementPage.s12.b1.body' },
      },
      {
        type: 'box',
        number: '12.2',
        titleId: 'AgreementPage.s12.b2.title',
        body: { id: 'AgreementPage.s12.b2.body' },
      },
      {
        type: 'box',
        number: '12.3',
        titleId: 'AgreementPage.s12.b3.title',
        body: { id: 'AgreementPage.s12.b3.body' },
      },
      {
        type: 'box',
        number: '12.4',
        titleId: 'AgreementPage.s12.b4.title',
        body: { id: 'AgreementPage.s12.b4.body' },
      },
      {
        type: 'box',
        number: '12.5',
        titleId: 'AgreementPage.s12.b5.title',
        body: { id: 'AgreementPage.s12.b5.body' },
      },
    ],
  },
  {
    number: '13',
    titleId: 'AgreementPage.s13.title',
    gradient: true,
    blocks: [
      {
        type: 'box',
        number: '13.1',
        titleId: 'AgreementPage.s13.b1.title',
        body: { id: 'AgreementPage.s13.b1.body' },
      },
      {
        type: 'box',
        number: '13.2',
        titleId: 'AgreementPage.s13.b2.title',
        body: { id: 'AgreementPage.s13.b2.body' },
      },
      {
        type: 'box',
        number: '13.3',
        titleId: 'AgreementPage.s13.b3.title',
        body: { id: 'AgreementPage.s13.b3.body' },
      },
      {
        type: 'box',
        number: '13.4',
        titleId: 'AgreementPage.s13.b4.title',
        body: { id: 'AgreementPage.s13.b4.body' },
      },
      {
        type: 'box',
        number: '13.5',
        titleId: 'AgreementPage.s13.b5.title',
        body: { id: 'AgreementPage.s13.b5.body' },
      },
    ],
  },
  {
    number: '14',
    titleId: 'AgreementPage.s14.title',
    blocks: [
      { type: 'p', id: 'AgreementPage.s14.p1' },
      { type: 'p', id: 'AgreementPage.s14.p2' },
    ],
  },
  {
    number: '15',
    titleId: 'AgreementPage.s15.title',
    blocks: [
      {
        type: 'box',
        number: '15.1',
        titleId: 'AgreementPage.s15.b1.title',
        body: { id: 'AgreementPage.s15.b1.body' },
      },
      {
        type: 'box',
        number: '15.2',
        titleId: 'AgreementPage.s15.b2.title',
        body: { id: 'AgreementPage.s15.b2.body' },
      },
      {
        type: 'box',
        number: '15.3',
        titleId: 'AgreementPage.s15.b3.title',
        body: { id: 'AgreementPage.s15.b3.body' },
      },
    ],
  },
  {
    number: '16',
    titleId: 'AgreementPage.s16.title',
    gradient: true,
    blocks: [
      {
        type: 'box',
        number: '16.1',
        titleId: 'AgreementPage.s16.b1.title',
        body: {
          introId: 'AgreementPage.s16.b1.intro',
          list: [
            'AgreementPage.s16.b1.list1',
            'AgreementPage.s16.b1.list2',
            'AgreementPage.s16.b1.list3',
            'AgreementPage.s16.b1.list4',
            'AgreementPage.s16.b1.list5',
          ],
        },
      },
      {
        type: 'box',
        number: '16.2',
        titleId: 'AgreementPage.s16.b2.title',
        body: { id: 'AgreementPage.s16.b2.body' },
      },
      {
        type: 'box',
        number: '16.3',
        titleId: 'AgreementPage.s16.b3.title',
        body: { id: 'AgreementPage.s16.b3.body' },
      },
    ],
  },
  {
    number: '17',
    titleId: 'AgreementPage.s17.title',
    accentBar: true,
    blocks: [
      {
        type: 'box',
        number: '17.1',
        titleId: 'AgreementPage.s17.b1.title',
        body: { id: 'AgreementPage.s17.b1.body' },
      },
      {
        type: 'box',
        number: '17.2',
        titleId: 'AgreementPage.s17.b2.title',
        body: { id: 'AgreementPage.s17.b2.body' },
      },
      {
        type: 'box',
        number: '17.3',
        titleId: 'AgreementPage.s17.b3.title',
        body: {
          introId: 'AgreementPage.s17.b3.intro',
          list: [
            'AgreementPage.s17.b3.list1',
            'AgreementPage.s17.b3.list2',
            'AgreementPage.s17.b3.list3',
            'AgreementPage.s17.b3.list4',
            'AgreementPage.s17.b3.list5',
            'AgreementPage.s17.b3.list6',
          ],
          outroId: 'AgreementPage.s17.b3.outro',
        },
      },
      {
        type: 'box',
        number: '17.4',
        titleId: 'AgreementPage.s17.b4.title',
        body: { id: 'AgreementPage.s17.b4.body' },
      },
    ],
  },
  {
    number: '18',
    titleId: 'AgreementPage.s18.title',
    blocks: [
      {
        type: 'box',
        number: '18.1',
        titleId: 'AgreementPage.s18.b1.title',
        body: {
          introId: 'AgreementPage.s18.b1.intro',
          list: [
            'AgreementPage.s18.b1.list1',
            'AgreementPage.s18.b1.list2',
            'AgreementPage.s18.b1.list3',
            'AgreementPage.s18.b1.list4',
            'AgreementPage.s18.b1.list5',
            'AgreementPage.s18.b1.list6',
          ],
        },
      },
      {
        type: 'box',
        number: '18.2',
        titleId: 'AgreementPage.s18.b2.title',
        body: { id: 'AgreementPage.s18.b2.body' },
      },
      {
        type: 'box',
        number: '18.3',
        titleId: 'AgreementPage.s18.b3.title',
        body: { id: 'AgreementPage.s18.b3.body' },
      },
      {
        type: 'box',
        number: '18.4',
        titleId: 'AgreementPage.s18.b4.title',
        body: { id: 'AgreementPage.s18.b4.body' },
      },
    ],
  },
  {
    number: '19',
    titleId: 'AgreementPage.s19.title',
    gradient: true,
    blocks: [
      { type: 'p', id: 'AgreementPage.s19.p1' },
      { type: 'p', id: 'AgreementPage.s19.p2' },
      { type: 'p', id: 'AgreementPage.s19.p3' },
    ],
  },
  {
    number: '20',
    titleId: 'AgreementPage.s20.title',
    accentBar: true,
    blocks: [
      { type: 'p', id: 'AgreementPage.s20.p1' },
      { type: 'p', id: 'AgreementPage.s20.p2' },
    ],
  },
  {
    number: '21',
    titleId: 'AgreementPage.s21.title',
    blocks: [
      {
        type: 'box',
        number: '21.1',
        titleId: 'AgreementPage.s21.b1.title',
        body: { id: 'AgreementPage.s21.b1.body' },
      },
      {
        type: 'box',
        number: '21.2',
        titleId: 'AgreementPage.s21.b2.title',
        body: { id: 'AgreementPage.s21.b2.body' },
      },
      {
        type: 'box',
        number: '21.3',
        titleId: 'AgreementPage.s21.b3.title',
        body: { id: 'AgreementPage.s21.b3.body' },
      },
      {
        type: 'box',
        number: '21.4',
        titleId: 'AgreementPage.s21.b4.title',
        body: { id: 'AgreementPage.s21.b4.body' },
      },
      {
        type: 'box',
        number: '21.5',
        titleId: 'AgreementPage.s21.b5.title',
        body: { id: 'AgreementPage.s21.b5.body' },
      },
      {
        type: 'box',
        number: '21.6',
        titleId: 'AgreementPage.s21.b6.title',
        body: { id: 'AgreementPage.s21.b6.body' },
      },
      {
        type: 'box',
        number: '21.7',
        titleId: 'AgreementPage.s21.b7.title',
        body: { id: 'AgreementPage.s21.b7.body' },
      },
      {
        type: 'box',
        number: '21.8',
        titleId: 'AgreementPage.s21.b8.title',
        body: { id: 'AgreementPage.s21.b8.body' },
      },
      {
        type: 'box',
        number: '21.9',
        titleId: 'AgreementPage.s21.b9.title',
        body: { id: 'AgreementPage.s21.b9.body' },
      },
    ],
  },
  {
    number: '22',
    titleId: 'AgreementPage.s22.title',
    gradient: true,
    blocks: [{ type: 'p', id: 'AgreementPage.s22.b1' }],
  },
  {
    number: '23',
    titleId: 'AgreementPage.s23.title',
    accentBar: true,
    blocks: [
      { type: 'p', id: 'AgreementPage.s23.intro' },
      {
        type: 'box',
        number: '23.1',
        titleId: 'AgreementPage.s23.b1.title',
        body: { id: 'AgreementPage.s23.b1.body' },
      },
      {
        type: 'box',
        number: '23.2',
        titleId: 'AgreementPage.s23.b2.title',
        body: { id: 'AgreementPage.s23.b2.body' },
      },
      {
        type: 'box',
        number: '23.3',
        titleId: 'AgreementPage.s23.b3.title',
        body: { id: 'AgreementPage.s23.b3.body' },
      },
      {
        type: 'box',
        number: '23.4',
        titleId: 'AgreementPage.s23.b4.title',
        body: { id: 'AgreementPage.s23.b4.body' },
      },
    ],
  },
  {
    number: '24',
    titleId: 'AgreementPage.s24.title',
    blocks: [
      {
        type: 'contact',
        cards: [
          {
            headingId: 'AgreementPage.s24.card1.heading',
            rows: [{ value: 'Metta People Co., Ltd. (บริษัท เมตตา พีเพีล จำกัด)' }],
          },
          {
            headingId: 'AgreementPage.s24.card2.heading',
            rows: [
              {
                value:
                  '41/1, Soi On Nut 36, On Nut Sub-district, Suan Luang District, Bangkok 10250, Thailand',
              },
            ],
          },
          {
            headingId: 'AgreementPage.s24.card3.heading',
            rows: [{ value: 'info@mettapeople.com' }],
          },
          {
            headingId: 'AgreementPage.s24.card4.heading',
            rows: [{ value: 'www.mettapeople.com' }],
          },
        ],
      },
    ],
  },
];
